import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import NavWordmark from "./NavWordmark";
import { EASE_OUT } from "../lib/motion";

// Role-first navigation (V2 overhaul): the primary nav exposes ONLY the active
// mode's world — one decision at a time. Admin jumps straight to its console.
const NAV_PUBLIC = [
  { to: "/find", label: "Find Parking" },
  { to: "/#how-it-works", label: "How It Works", hash: true },
  { to: "/support", label: "Support" },
];
const NAV_DRIVER = [
  { to: "/find", label: "Find Parking" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/bookings", label: "Bookings" },
  { to: "/vehicles", label: "Vehicles" },
];
const NAV_HOST = [
  { to: "/host", label: "Dashboard" },
  { to: "/host/bookings", label: "Bookings" },
  { to: "/payouts", label: "Payouts" },
  { to: "/host/invoices", label: "Earnings" },
];
const NAV_COMMERCIAL = [
  { to: "/commercial", label: "Dashboard" },
  { to: "/commercial/properties", label: "Properties" },
  { to: "/commercial/bookings", label: "Bookings" },
];

export default function Navbar() {
  const { user, loading, logout, isAdmin, isSupport, mode, switchMode } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  // Cinematic nav (V2 Part 16): on the homepage the bar participates in the
  // story — transparent over the hero, compact + more translucent through the
  // 3D scenes, restored at the final CTA. Other pages keep standard behavior.
  const { pathname } = useLocation();
  const [cineDepth, setCineDepth] = useState(0); // 0 hero · 1 story · 2 outro
  useEffect(() => {
    if (pathname !== "/") { setCineDepth(0); return; }
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const vh = window.innerHeight;
        const y = window.scrollY;
        setCineDepth(y < vh * 0.35 ? 0 : y < vh * 4.4 ? 1 : 2);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [pathname]);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const navState = cineDepth === 1 && !open
    ? "bg-[#080B0D]/50 backdrop-blur-2xl border-b border-transparent"
    : scrolled || open ? "bg-[#080B0D]/85 backdrop-blur-xl border-b border-[#1a1d21]" : "bg-transparent border-b border-transparent";
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = !user ? NAV_PUBLIC
    : isAdmin ? [{ to: "/admin", label: "Admin Console" }]
    : mode === "host" ? NAV_HOST
    : mode === "commercial" ? NAV_COMMERCIAL
    : NAV_DRIVER;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenu(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    let live = true;
    api.unreadCount().then((r) => live && setUnread(r.unread)).catch(() => {});
    return () => { live = false; };
  }, [user]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const goHash = (e, to) => {
    if (!to.includes("#")) return;
    e.preventDefault();
    setOpen(false);
    const [path, hash] = to.split("#");
    if (window.location.pathname !== (path || "/")) navigate(path || "/");
    setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  const linkCls = ({ isActive }) =>
    `relative text-[0.82rem] font-medium tracking-wide transition-colors duration-200 ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${navState}`} data-testid="navbar">
      <nav className={`max-w-7xl mx-auto px-5 sm:px-8 ${cineDepth === 1 ? "h-16" : "h-[4.75rem]"} transition-[height] duration-500 flex items-center justify-between`} aria-label="Main">
        <NavWordmark />
        <div className="hidden lg:flex items-center gap-9">
          {navItems.map((n) =>
            n.hash ? (
              <a key={n.label} href={n.to} onClick={(e) => goHash(e, n.to)} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                 className="text-[0.82rem] font-medium tracking-wide text-zinc-400 hover:text-white transition-colors duration-200">{n.label}</a>
            ) : (
              <NavLink key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && <motion.span layoutId="nav-underline" className="absolute -bottom-[7px] left-0 right-0 h-px bg-[#E50914]" transition={{ duration: 0.3, ease: EASE_OUT }} />}
                  </>
                )}
              </NavLink>
            )
          )}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          {loading ? null : user ? (
            <>
              <div className="flex items-center gap-1 p-1 rounded-xl border border-[#27272A] bg-[#0D1114]/80" data-testid="mode-switch" role="tablist" aria-label="Active mode">
                {[["driver", "Driver", "/dashboard"], ["host", "Host", "/host"],
                  ...(user?.commercial_access === "active" ? [["commercial", "Commercial", "/commercial"]] : [])].map(([v, l, dest]) => (
                  <button key={v} role="tab" aria-selected={mode === v} data-testid={`mode-switch-${v}`}
                          onClick={() => { switchMode(v); navigate(dest); }}
                          className={`px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold tracking-[0.1em] uppercase transition-colors duration-200 ${mode === v ? "bg-[#E50914] text-white" : "text-zinc-500 hover:text-zinc-200"}`}>{l}</button>
                ))}
              </div>
              <Link to="/notifications" data-testid="nav-notifications" aria-label="Notifications"
                    className="relative p-2 rounded-lg hover:bg-white/5 transition-colors">
                <Bell className="w-[1.1rem] h-[1.1rem] text-zinc-300" />
                {unread > 0 && <span data-testid="nav-unread-badge" className="absolute top-0.5 right-0.5 bg-[#E50914] text-white text-[9px] font-bold rounded-full min-w-4 h-4 flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
              </Link>
              <div className="relative" ref={menuRef}>
                <button data-testid="nav-user-menu-btn" onClick={() => setMenu((m) => !m)} aria-expanded={menu} aria-haspopup="menu"
                        className="flex items-center gap-2.5 border border-[#27272A] rounded-lg pl-1.5 pr-3 py-1.5 hover:border-[#3F3F46] transition-colors">
                  <span className="w-7 h-7 rounded-md bg-[#E50914]/15 text-[#FF3B45] flex items-center justify-center text-xs font-bold">
                    {(user.name || user.email || "U")[0].toUpperCase()}
                  </span>
                  <span className="text-[0.82rem] font-medium max-w-28 truncate">{user.name || user.email}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${menu ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {menu && (
                    <motion.div role="menu" data-testid="nav-user-dropdown"
                                initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                transition={{ duration: 0.18, ease: EASE_OUT }}
                                className="absolute right-0 mt-2 w-60 card-dark p-1.5 shadow-2xl origin-top-right max-h-[calc(100dvh-6rem)] overflow-y-auto">
                      {[
                        ...(mode === "commercial"
                          ? [["/commercial", "Commercial Dashboard"], ["/commercial/properties", "Properties"], ["/commercial/properties/new", "Add Property"], ["/commercial/bookings", "My Bookings"], ["/payouts", "Payouts"], ["/support", "Support"]]
                          : mode === "host"
                          ? [["/host", "Host Dashboard"], ["/host/listings/new", "Add Parking Space"], ["/host/bookings", "Host Bookings"], ["/host/availability", "Availability"], ["/host/invoices", "Host Invoices"], ["/payouts", "Payouts"], ["/support", "Support"]]
                          : [["/dashboard", "Dashboard"], ["/find", "Find Parking"], ["/bookings", "My Bookings"], ["/vehicles", "My Vehicles"], ["/invoices", "Invoices"], ["/wallet", "Wallet"], ["/support", "Support"]]),
                        ...(user?.security_access_count > 0 ? [["/security", "Shared Properties"]] : []),
                        isSupport && ["/support-console", "Support Console"],
                        isAdmin && ["/admin", "Admin"],
                        ["/profile", "Profile & Settings"],
                      ].filter(Boolean).map(([to, label]) => (
                        <Link key={to} to={to} role="menuitem" data-testid={`menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
                              onClick={() => setMenu(false)}
                              className="block px-3.5 py-2.5 rounded-lg text-[0.82rem] text-zinc-300 hover:bg-white/5 hover:text-white transition-colors">{label}</Link>
                      ))}
                      <div className="my-1.5 rule" />
                      <button role="menuitem" data-testid="menu-logout" onClick={logout}
                              className="w-full text-left px-3.5 py-2.5 rounded-lg text-[0.82rem] text-[#FF3B45] hover:bg-[#E50914]/10 transition-colors">
                        Log Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-[0.82rem] font-medium tracking-wide text-zinc-400 hover:text-white transition-colors px-2 py-2">Sign In</Link>
              <Link to="/register" data-testid="nav-get-started" className="btn-primary !py-2.5 !px-6 !text-[0.82rem] !rounded-xl">Get Started</Link>
            </>
          )}
        </div>
        <button className="lg:hidden p-2 -mr-2" data-testid="nav-mobile-toggle" aria-label="Toggle menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <div className="w-5 flex flex-col gap-[5px]">
            <motion.span animate={open ? { rotate: 45, y: 3.5 } : { rotate: 0, y: 0 }} transition={{ duration: 0.2 }} className="block h-px bg-white" />
            <motion.span animate={open ? { rotate: -45, y: -3.5 } : { rotate: 0, y: 0 }} transition={{ duration: 0.2 }} className="block h-px bg-white" />
          </div>
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.28, ease: EASE_OUT }}
                      className="lg:hidden overflow-hidden border-b border-[#1a1d21] bg-[#080B0D]/95 backdrop-blur-xl" data-testid="nav-mobile-menu">
            <div className="px-5 py-5 space-y-1 max-h-[calc(100dvh-4.75rem)] overflow-y-auto overscroll-contain">
              {navItems.map((n, i) => (
                <motion.div key={n.label} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.04, duration: 0.3, ease: EASE_OUT }}>
                  <Link to={n.hash ? "/" : n.to} onClick={(e) => { n.hash ? goHash(e, n.to) : setOpen(false); }}
                        className="block px-2 py-3.5 text-lg font-display font-medium text-zinc-200 hover:text-white transition-colors">{n.label}</Link>
                </motion.div>
              ))}
              {user ? (
                <>
                  <div className={`grid ${user?.commercial_access === "active" ? "grid-cols-3" : "grid-cols-2"} gap-1 p-1 rounded-xl border border-[#27272A] bg-[#0D1114]/80 mb-2`} data-testid="mode-switch-mobile" role="tablist" aria-label="Active mode">
                    {[["driver", "Driver", "/dashboard"], ["host", "Host", "/host"],
                      ...(user?.commercial_access === "active" ? [["commercial", "Commercial", "/commercial"]] : [])].map(([v, l, dest]) => (
                      <button key={v} role="tab" aria-selected={mode === v} data-testid={`mode-switch-mobile-${v}`}
                              onClick={() => { switchMode(v); setOpen(false); navigate(dest); }}
                              className={`py-2.5 rounded-lg text-xs font-bold tracking-[0.1em] uppercase transition-colors ${mode === v ? "bg-[#E50914] text-white" : "text-zinc-500"}`}>{l}</button>
                    ))}
                  </div>
                  {(mode === "commercial"
                    ? [["/commercial", "Commercial Dashboard"], ["/commercial/properties", "Properties"], ["/commercial/properties/new", "Add Property"], ["/commercial/bookings", "My Bookings"], ["/payouts", "Payouts"]]
                    : mode === "host"
                    ? [["/host", "Host Dashboard"], ["/host/listings/new", "Add Parking Space"], ["/host/bookings", "Host Bookings"], ["/host/availability", "Availability"], ["/host/invoices", "Host Invoices"], ["/payouts", "Payouts"]]
                    : [["/dashboard", "Dashboard"], ["/find", "Find Parking"], ["/bookings", "My Bookings"], ["/vehicles", "My Vehicles"], ["/invoices", "Invoices"], ["/wallet", "Wallet"]]
                  ).map(([to, label]) => (
                    <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block px-2 py-3.5 text-lg font-display font-medium text-zinc-200">{label}</Link>
                  ))}
                  {user?.security_access_count > 0 && (
                    <Link to="/security" onClick={() => setOpen(false)} data-testid="mobile-menu-security"
                          className="block px-2 py-3.5 text-lg font-display font-medium text-zinc-200">Shared Properties</Link>
                  )}
                  {isSupport && (
                    <Link to="/support-console" onClick={() => setOpen(false)} data-testid="mobile-menu-support-console"
                          className="block px-2 py-3.5 text-lg font-display font-medium text-zinc-200">Support Console</Link>
                  )}
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-menu-admin"
                          className="block px-2 py-3.5 text-lg font-display font-medium text-[#FF3B45]">Admin</Link>
                  )}
                  <Link to="/profile" onClick={() => setOpen(false)} className="block px-2 py-3.5 text-lg font-display font-medium text-zinc-200">Profile</Link>
                  <button data-testid="nav-mobile-logout" onClick={logout} className="block w-full text-left px-2 py-3.5 text-lg font-display font-medium text-[#FF3B45]">Log Out</button>
                </>
              ) : (
                <div className="flex gap-3 pt-4">
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary flex-1 text-sm">Sign In</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-primary flex-1 text-sm">Get Started</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
